import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import styled from 'styled-components';

import { formatDayMonthYear } from '^/components/molecules/PhotoList/util';
import { useL10n } from '^/hooks';
import {
  useRequestDroneCapture,
  useV2BAlbumByIdQuery,
} from '^/hooks/api/viewpoint-capture/useViewpoint';
import { useViewpointStore } from '^/store/viewpointStore';
import { RequestedInfo } from './RequestedInfo';
import { PhotoPairItem } from './PhotoPairItem';
import { NoViewpointsInfo } from './NoViewpointsInfo';
import { ViewpointPhotoListHeader } from './ViewpointPhotoListHeader';
import Button from '^/components/atoms/Button';
import CheckCircleIcon from '^/assets/icons/photo-request/check-circle.svg';
import theme from '^/theme';
import * as T from '^/types';
import { SkeletonLoader } from './SkeletonLoader';
import Text from './text';
import { usePopupStore } from '^/store/popup';
import { VIEWPOINTS_IMAGE_PER_REQUEST } from '^/constants/data';

const DateText = styled.div({
  color: theme.colorTokens.textCool,
  fontSize: '10px',
  padding: '8px 12px',
  margin: '8px 0px',
});

const ScrollableContainer = styled.div({
  overflowY: 'auto',
  flex: 1,
  padding: '0 20px',
  height: '100%',
});

const ViewpointListContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const ButtonContainer = styled.div({
  padding: '16px 20px',
});

export function ViewpointCapturePhotosList() {
  const [l10n] = useL10n();
  const { contentId } = useParams();
  const selectedViewpointAlbumId = Number(contentId);

  const { data: viewPointAlbum, isLoading } = useV2BAlbumByIdQuery(selectedViewpointAlbumId ?? NaN);

  const viewPointPhotos = viewPointAlbum?.photos;

  const { isLoading: isRequestLoading } = useRequestDroneCapture();
  const isViewpointUploading = useViewpointStore(s => s.isViewpointUploading);
  const setDeletingViewpointId = useViewpointStore(s => s.setDeletingViewpointId);

  const loaderRef = useRef<HTMLDivElement>(null);

  const setMinorPopup = usePopupStore(s => s.setMinorPopup);

  useEffect(() => {
    if (isViewpointUploading) {
      loaderRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isViewpointUploading]);

  const handleRequestPhoto = () => {
    if (!selectedViewpointAlbumId) {
      return;
    }
    setMinorPopup(T.ContentPageMinorPopupType.REQUEST_PHOTOS_CONFIRMATION);
  };

  const handleDeletePhoto = (photoId: number) => {
    if (!selectedViewpointAlbumId) {
      return;
    }
    setDeletingViewpointId(photoId);
    setMinorPopup(T.ContentPageMinorPopupType.CONFIRM_DELETE_VIEWPOINTS);
  };

  const isPhotoRequested = viewPointAlbum?.status === T.RequestPhotoStatus.REQUESTED;
  const isCompleted = viewPointAlbum?.status === T.RequestPhotoStatus.COMPLETED;
  const isFailed = viewPointAlbum?.status === T.RequestPhotoStatus.FAILED;

  const tooltipContent = useMemo(() => {
    if (isPhotoRequested) {
      return l10n(Text.alreadyRequested);
    }
    if (viewPointPhotos && viewPointPhotos?.length > VIEWPOINTS_IMAGE_PER_REQUEST) {
      return l10n(Text.freeLimit);
    }
    return '';
  }, [viewPointPhotos]);

  const shouldRequestButtonBeDisabled =
    Boolean(tooltipContent) ||
    !viewPointPhotos?.length ||
    isRequestLoading ||
    isPhotoRequested ||
    isCompleted ||
    isFailed ||
    isViewpointUploading;

  const albumTakenAt = formatDayMonthYear(viewPointAlbum?.takenAt ?? new Date(), T.Language.EN_US);

  return (
    <Fragment>
      {viewPointAlbum && <ViewpointPhotoListHeader selectedViewpointAlbum={viewPointAlbum} />}

      {isPhotoRequested && <RequestedInfo />}

      <ScrollableContainer>
        {isLoading && (
          <SkeletonLoader
            count={3}
            containerCustomStyles={{
              marginTop: '65px',
            }}
          />
        )}
        {viewPointPhotos?.length || isViewpointUploading ? (
          <>
            <DateText>{albumTakenAt}</DateText>
            <ViewpointListContainer>
              {viewPointPhotos?.map((viewpoint, index) => (
                <PhotoPairItem
                  key={viewpoint.id}
                  viewpoint={viewpoint}
                  index={index}
                  requestTitle={viewPointAlbum?.title || ''}
                  requestStatus={viewPointAlbum?.status ?? T.RequestPhotoStatus.PENDING}
                  onDelete={handleDeletePhoto}
                />
              ))}
              {isViewpointUploading ? (
                <div ref={loaderRef}>
                  <SkeletonLoader count={1} />
                </div>
              ) : null}
            </ViewpointListContainer>
          </>
        ) : (
          <NoViewpointsInfo />
        )}
      </ScrollableContainer>

      <ButtonContainer>
        <Tippy
          content={tooltipContent}
          theme="angelsw"
          disabled={!tooltipContent}
          maxWidth={200}
          arrow={false}
        >
          <Button
            disabled={shouldRequestButtonBeDisabled}
            onClick={handleRequestPhoto}
            size="large"
            customStyles={{
              width: '100%',
              ...(isPhotoRequested && {
                color: theme.colorTokens.textCool,
                gap: '8px',
                backgroundColor: theme.colors.white[100],
                border: `1px solid ${theme.colorTokens.lineCool}`,
              }),
            }}
          >
            {isPhotoRequested ? (
              <>
                <CheckCircleIcon />
                {l10n(Text.photosRequested)}
              </>
            ) : (
              `${l10n(Text.requestPhotos)} (${
                viewPointPhotos?.length
              }/${VIEWPOINTS_IMAGE_PER_REQUEST})`
            )}
          </Button>
        </Tippy>
      </ButtonContainer>
    </Fragment>
  );
}
